import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { LanguageSelectorComponent } from '../../shared/language-selector/language-selector.component';


@Component({
    selector: 'app-sign-up-space',
    imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, FormsModule, LanguageSelectorComponent],
    templateUrl: './sign-up-space.component.html',
    styleUrl: './sign-up-space.component.scss'
})
export class SignUpSpaceComponent implements OnInit {
  signupForm!: FormGroup;
  loading = false;
  submitted = false;
  showPassword = false;
  showConfirmPassword = false;
  showExtra = true;

  // Phone country codes (common list)
  countryCodes = [
    // Core
    { code: '+1',   label: '🇺🇸 +1 (US/CA)' },
    { code: '+33',  label: '🇫🇷 +33 (FR)' },
    { code: '+32',  label: '🇧🇪 +32 (BE)' },
    { code: '+41',  label: '🇨🇭 +41 (CH)' },
    { code: '+49',  label: '🇩🇪 +49 (DE)' },
    { code: '+39',  label: '🇮🇹 +39 (IT)' },
    { code: '+34',  label: '🇪🇸 +34 (ES)' },
    { code: '+351', label: '🇵🇹 +351 (PT)' },
    { code: '+44',  label: '🇬🇧 +44 (UK)' },
    { code: '+352', label: '🇱🇺 +352 (LU)' },
    { code: '+31',  label: '🇳🇱 +31 (NL)' },
    { code: '+46',  label: '🇸🇪 +46 (SE)' },
    { code: '+47',  label: '🇳🇴 +47 (NO)' },
    { code: '+45',  label: '🇩🇰 +45 (DK)' },
    { code: '+420', label: '🇨🇿 +420 (CZ)' },
    { code: '+48',  label: '🇵🇱 +48 (PL)' },
    { code: '+40',  label: '🇷🇴 +40 (RO)' },
    { code: '+30',  label: '🇬🇷 +30 (GR)' },
    { code: '+386', label: '🇸🇮 +386 (SI)' },
    { code: '+421', label: '🇸🇰 +421 (SK)' },
    { code: '+380', label: '🇺🇦 +380 (UA)' },
    { code: '+7',   label: '🇷🇺 +7 (RU)' },
    { code: '+81',  label: '🇯🇵 +81 (JP)' },
    { code: '+82',  label: '🇰🇷 +82 (KR)' },
    { code: '+86',  label: '🇨🇳 +86 (CN)' },
    { code: '+91',  label: '🇮🇳 +91 (IN)' },
    // North/West/Central Africa
    { code: '+212', label: '🇲🇦 +212 (MA)' },
    { code: '+216', label: '🇹🇳 +216 (TN)' },
    { code: '+213', label: '🇩🇿 +213 (DZ)' },
    { code: '+225', label: '🇨🇮 +225 (CI)' },
    { code: '+221', label: '🇸🇳 +221 (SN)' },
    { code: '+234', label: '🇳🇬 +234 (NG)' }, // Nigeria
    { code: '+244', label: '🇦🇴 +244 (AO)' }, // Angola
    { code: '+241', label: '🇬🇦 +241 (GA)' }, // Gabon
    { code: '+242', label: '🇨🇬 +242 (CG)' }, // Congo-Brazzaville
    { code: '+258', label: '🇲🇿 +258 (MZ)' }, // Mozambique
    // Middle East / Arab states
    { code: '+974', label: '🇶🇦 +974 (QA)' }, // Qatar
    { code: '+966', label: '🇸🇦 +966 (SA)' }, // Saudi Arabia
    { code: '+971', label: '🇦🇪 +971 (AE)' }, // UAE
    { code: '+20',  label: '🇪🇬 +20 (EG)' },  // Egypt
    { code: '+962', label: '🇯🇴 +962 (JO)' }, // Jordan
    { code: '+961', label: '🇱🇧 +961 (LB)' }, // Lebanon
    { code: '+965', label: '🇰🇼 +965 (KW)' }, // Kuwait
    { code: '+973', label: '🇧🇭 +973 (BH)' }, // Bahrain
    { code: '+968', label: '🇴🇲 +968 (OM)' }, // Oman
    { code: '+964', label: '🇮🇶 +964 (IQ)' }, // Iraq
    { code: '+963', label: '🇸🇾 +963 (SY)' }, // Syria
    { code: '+967', label: '🇾🇪 +967 (YE)' }, // Yemen
    { code: '+970', label: '🇵🇸 +970 (PS)' }, // Palestine
    { code: '+218', label: '🇱🇾 +218 (LY)' }, // Libya
    { code: '+249', label: '🇸🇩 +249 (SD)' }, // Sudan
    { code: '+222', label: '🇲🇷 +222 (MR)' }, // Mauritania
    // Americas
    { code: '+55',  label: '🇧🇷 +55 (BR)' }, // Brazil
    // Others requested explicitly
    { code: '+972', label: '🇮🇱 +972 (IL)' }  // Israel
  ];

  // Avatar/crop state
  avatarMode: 'url' | 'upload' = 'upload';
  avatarPreview: string | null = null; // original preview
  avatarCroppedDataUrl: string | null = null; // 200x200 result
  cropScale = 1.0; // 1.0 - 2.0
  cropShiftX = 0;  // -100 .. 100 (percent of max pan)
  cropShiftY = 0;  // -100 .. 100
  private uploadedImage?: HTMLImageElement;
  showCropModal = false;

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.signupForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      acceptTerms: [false, Validators.requiredTrue],
      phoneCountryCode: ['+33'],
      phone: ['', [this.optionalPhoneValidator]],
      company: [''],
      jobTitle: [''],
      avatarUrl: ['']
    }, {
      validators: this.passwordMatchValidator
    });
  }

  // Validateur personnalisé pour vérifier que les mots de passe correspondent
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ matching: true });
      return { matching: true };
    }
    
    return null;
  }

  // Getter pour un accès facile aux champs du formulaire
  get f(): any { return this.signupForm.controls; }

  onSubmit() {
    this.submitted = true;

    if (this.signupForm.invalid) {
      return;
    }

    this.loading = true;

  // Only send an avatar URL if it's a web URL to keep payload small; cropped data URL is for preview only
  const rawAvatar: string = this.f['avatarUrl'].value;
  const avatarUrl = (rawAvatar && /^https?:\/\//i.test(rawAvatar)) ? rawAvatar : undefined;
    this.authService.register(
      this.f['firstName'].value,
      this.f['lastName'].value,
      this.f['email'].value,
      this.f['email'].value, // Utilisé comme username
      this.f['password'].value,
      {
        phoneCountryCode: this.f['phoneCountryCode'].value,
        phone: this.f['phone'].value,
        company: this.f['company'].value,
        jobTitle: this.f['jobTitle'].value,
        avatarUrl,
      }
    ).subscribe({
      next: data => {
        this.loading = false;
        // Rediriger vers la page de connexion avec un message de succès
        this.router.navigate(['/login']);
      },
      error: err => {
        console.error(err);
        this.loading = false;
        // Afficher un message d'erreur
      }
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Générer l'URL correcte en fonction de la langue (comme dans nav-bar)
  getRouterLink(path: string): any[] {
    const currentLang = this.languageService.getCurrentLanguage();
    
    // Pour la page d'accueil
    if (path === '') {
      return currentLang === 'en' ? ['/'] : ['/', currentLang];
    }
    
    // Pour les autres pages
    return currentLang === 'en' ? ['/', path] : ['/', currentLang, path];
  }

  // Optional phone validator: valid if empty; otherwise basic phone chars 6-20
  optionalPhoneValidator = (control: AbstractControl): ValidationErrors | null => {
    const val: string = control.value || '';
    if (!val) return null;
    const ok = /^[0-9\s\-\.]{6,20}$/.test(val);
    return ok ? null : { phone: true };
  };

  // Avatar upload handling
  onAvatarFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        this.uploadedImage = img;
        this.avatarPreview = reader.result as string;
        this.cropScale = 1.0;
        this.cropShiftX = 0;
  this.cropShiftY = 0;
  this.drawCroppedAvatar();
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  onAvatarUrlChange() {
    const url = this.f['avatarUrl'].value;
    if (!url) { this.avatarPreview = null; this.avatarCroppedDataUrl = null; return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.uploadedImage = img;
      this.avatarPreview = url;
      this.cropScale = 1.0;
      this.cropShiftX = 0;
      this.cropShiftY = 0;
      this.drawCroppedAvatar();
    };
    img.onerror = () => { /* silently ignore */ };
    img.src = url;
  }

  onCropParamsChange() { this.drawCroppedAvatar(); }

  private drawCroppedAvatar() {
    if (!this.uploadedImage) { this.avatarCroppedDataUrl = this.avatarPreview; return; }
    const img = this.uploadedImage;
    const canvas = document.createElement('canvas');
    const size = 200;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = Math.max(1, Math.min(2, this.cropScale));
    const drawW = img.naturalWidth * scale;
    const drawH = img.naturalHeight * scale;
    // Max pan so edges stay within the square
    const maxPanX = Math.max(0, (drawW - size) / 2);
    const maxPanY = Math.max(0, (drawH - size) / 2);
    const dx = (this.cropShiftX / 100) * maxPanX;
    const dy = (this.cropShiftY / 100) * maxPanY;
    const drawX = (size - drawW) / 2 - dx;
    const drawY = (size - drawH) / 2 - dy;

    // Cover behavior: ensure the drawn image fully covers the canvas
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    this.avatarCroppedDataUrl = canvas.toDataURL('image/png');
  }

  toggleExtra() { this.showExtra = !this.showExtra; }

  openCropModal() { if (this.uploadedImage || this.avatarPreview) this.showCropModal = true; }
  closeCropModal() { this.showCropModal = false; }

  async confirmCrop() {
    // Enforce 200x200 with compression
    if (!this.avatarCroppedDataUrl) { this.showCropModal = false; return; }
    const compressed = await this.compressDataUrl(this.avatarCroppedDataUrl, 200, 200, 0.9);
    this.avatarCroppedDataUrl = compressed;
    this.showCropModal = false;
  }

  removeAvatar() {
    this.avatarPreview = null;
    this.avatarCroppedDataUrl = null;
    this.uploadedImage = undefined;
    this.f['avatarUrl'].setValue('');
  }

  private async compressDataUrl(dataUrl: string, width: number, height: number, quality = 0.9): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(dataUrl); return; }
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        // Try JPEG first to reduce size; fallback to PNG
        const jpeg = canvas.toDataURL('image/jpeg', quality);
        if (jpeg && jpeg.length < dataUrl.length) resolve(jpeg);
        else resolve(canvas.toDataURL('image/png'));
      };
      img.src = dataUrl;
    });
  }
}